import { z } from 'zod';
import { subscriptionStatusSchema } from './platform';

/** Subscription billing contracts (ADR-0030 D2–D4, ADR-0033). Money in integer minor units. */

export const subscriptionInvoiceStatusSchema = z.enum(['open', 'paid', 'void']);
export type SubscriptionInvoiceStatus = z.infer<typeof subscriptionInvoiceStatusSchema>;

export const bankAccountSchema = z.object({
  bank_name: z.string().nullable(),
  account_number: z.string().nullable(),
  account_name: z.string().nullable(),
});

/** The legal entity issuing subscription invoices (platform settings `billing.issuer`). */
export const billingIssuerSchema = z.object({
  legal_name: z.string().nullable(),
  address: z.string().nullable(),
  tax_id: z.string().nullable(),
  bank_account: bankAccountSchema.nullable(),
});
export type BillingIssuer = z.infer<typeof billingIssuerSchema>;

/** How to pay: manual bank transfer quoting the invoice number (ADR-0030 D4). */
export const paymentInstructionsSchema = z.object({
  method: z.literal('bank_transfer'),
  provider: z.string(),
  bank_account: bankAccountSchema.nullable(),
  /** Text the customer must put in the transfer note. */
  reference_hint: z.string().nullable(),
});
export type PaymentInstructions = z.infer<typeof paymentInstructionsSchema>;

export const subscriptionInvoiceSchema = z.object({
  id: z.string(),
  number: z.string(),
  plan_code: z.string(),
  plan_name: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  currency: z.string(),
  subtotal_minor: z.number().int(),
  tax_rate_bps: z.number().int(),
  tax_minor: z.number().int(),
  total_minor: z.number().int(),
  status: subscriptionInvoiceStatusSchema,
  /** Open and past its due date. */
  overdue: z.boolean(),
  issued_at: z.string(),
  due_at: z.string(),
  paid_at: z.string().nullable(),
  payment_method: z.string().nullable(),
  payment_reference: z.string().nullable(),
  version: z.number().int(),
});
export type SubscriptionInvoice = z.infer<typeof subscriptionInvoiceSchema>;

export const subscriptionInvoiceListSchema = z.object({
  items: z.array(subscriptionInvoiceSchema),
});

/** Printable invoice: issuer, customer, amounts with PPN, and transfer instructions. */
export const subscriptionInvoiceDetailSchema = z.object({
  invoice: subscriptionInvoiceSchema,
  issuer: billingIssuerSchema,
  customer: z.object({
    name: z.string(),
    legal_name: z.string().nullable(),
    tax_id: z.string().nullable(),
    billing_email: z.string().nullable(),
  }),
  payment_instructions: paymentInstructionsSchema.nullable(),
});
export type SubscriptionInvoiceDetail = z.infer<typeof subscriptionInvoiceDetailSchema>;

const limitsSchema = z.object({
  max_branches: z.number().int().nullable(),
  max_staff: z.number().int().nullable(),
  max_devices: z.number().int().nullable(),
});

/** `GET /v1/tenants/:tenantId/subscription` */
export const subscriptionOverviewSchema = z.object({
  plan: z.object({
    code: z.string(),
    name: z.string(),
    price_minor: z.number().int(),
    currency: z.string(),
    interval: z.enum(['month', 'year']),
  }),
  status: subscriptionStatusSchema,
  trial_ends_at: z.string().nullable(),
  current_period_end: z.string().nullable(),
  /** Complimentary licence (ADR-0041): no invoices, nothing to pay. */
  complimentary: z.boolean(),
  modules: z.array(z.string()),
  limits: limitsSchema,
  usage: z.object({
    branches: z.number().int(),
    staff: z.number().int(),
    devices: z.number().int(),
  }),
  /** Sum of open invoices. */
  balance_due_minor: z.number().int(),
  /** When the account becomes read-only / suspended if nothing is paid (null = not overdue). */
  read_only_at: z.string().nullable(),
  suspended_at: z.string().nullable(),
  tax_rate_bps: z.number().int(),
  billing: z.object({
    legal_name: z.string().nullable(),
    billing_email: z.string().nullable(),
    tax_id: z.string().nullable(),
  }),
  issuer: billingIssuerSchema,
  payment_instructions: paymentInstructionsSchema,
});
export type SubscriptionOverview = z.infer<typeof subscriptionOverviewSchema>;

// ---- Platform console

export const platformSubscriptionInvoiceSchema = subscriptionInvoiceSchema.extend({
  tenant_id: z.string(),
  tenant_name: z.string(),
  tenant_slug: z.string(),
  recorded_by: z.string().nullable(),
});
export type PlatformSubscriptionInvoice = z.infer<typeof platformSubscriptionInvoiceSchema>;

export const platformSubscriptionInvoiceListSchema = z.object({
  items: z.array(platformSubscriptionInvoiceSchema),
});

export const platformInvoiceListQuerySchema = z.object({
  tenant_id: z.string().max(64).optional(),
  status: subscriptionInvoiceStatusSchema.optional(),
});

const isoInstant = z.iso.datetime({ offset: true });

/** Issue an invoice by hand (e.g. the first paid period, or after a plan change). */
export const issueSubscriptionInvoiceRequestSchema = z.object({
  tenant_id: z.string().min(1).max(64),
  /** Defaults to the tenant's plan. Paying the invoice moves the tenant onto this plan. */
  plan_code: z.string().min(1).max(40).optional(),
  /** Defaults to the end of the paid period, else the end of the trial, else now. */
  period_start: isoInstant.optional(),
});
export type IssueSubscriptionInvoiceRequest = z.input<typeof issueSubscriptionInvoiceRequestSchema>;

/** Record a manual bank transfer the operator has seen on the bank statement. */
export const recordSubscriptionPaymentRequestSchema = z.object({
  invoice_id: z.string().min(1).max(64),
  expected_version: z.number().int().positive(),
  /** Must equal the invoice total (no partial payments). */
  amount_minor: z.number().int().positive(),
  paid_at: isoInstant,
  method: z.literal('bank_transfer'),
  reference: z.string().trim().min(1).max(120),
});
export type RecordSubscriptionPaymentRequest = z.input<
  typeof recordSubscriptionPaymentRequestSchema
>;

export const voidSubscriptionInvoiceRequestSchema = z.object({
  expected_version: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});
export type VoidSubscriptionInvoiceRequest = z.input<typeof voidSubscriptionInvoiceRequestSchema>;

export const subscriptionPaymentResultSchema = z.object({
  invoice: platformSubscriptionInvoiceSchema,
  tenant_status: subscriptionStatusSchema,
  current_period_end: z.string().nullable(),
});
export type SubscriptionPaymentResult = z.infer<typeof subscriptionPaymentResultSchema>;

/** Run the billing cycle now (operators; `as_of` only outside production). */
export const runBillingCycleRequestSchema = z.object({
  as_of: isoInstant.optional(),
});

export const billingCycleResultSchema = z.object({
  as_of: z.string(),
  tenants: z.number().int(),
  invoices_issued: z.number().int(),
  reminders: z.number().int(),
  transitions: z.array(
    z.object({
      tenant_id: z.string(),
      from: subscriptionStatusSchema,
      to: subscriptionStatusSchema,
    }),
  ),
  failures: z.array(z.object({ tenant_id: z.string(), error: z.string() })),
});
export type BillingCycleResult = z.infer<typeof billingCycleResultSchema>;
